/**
 * usePersistentData Hook
 * Custom hook for managing persistent data across components
 */

import { useState, useEffect, useCallback } from 'react';
import persistentStorage from '../services/PersistentStorageService';

export const usePersistentData = () => {
  const [isReady, setIsReady] = useState(false);

  // Initialize persistent storage
  useEffect(() => {
    try {
      // Test storage availability
      const testData = { test: true };
      persistentStorage.setItem('__test__', testData);
      const retrieved = persistentStorage.getItem('__test__');
      persistentStorage.removeItem('__test__');
      
      if (retrieved && retrieved.test === true) {
        setIsReady(true);
        console.log('✅ Persistent storage initialized successfully');
      } else {
        console.warn('⚠️ Persistent storage test failed');
        setIsReady(true); // Still allow usage with fallbacks
      }
    } catch (error) {
      console.error('❌ Failed to initialize persistent storage:', error);
      setIsReady(true); // Allow usage with fallbacks
    }
  }, []);

  // Store form data
  const storeFormData = useCallback((formName, data) => {
    if (!isReady) return false;
    return persistentStorage.storeFormData(formName, data);
  }, [isReady]);

  // Get form data
  const getFormData = useCallback((formName) => {
    if (!isReady) return null;
    return persistentStorage.getFormData(formName);
  }, [isReady]);

  // Store user preferences
  const storeUserPreferences = useCallback((preferences) => {
    if (!isReady) return false;
    return persistentStorage.storeUserPreferences(preferences);
  }, [isReady]);

  // Get user preferences
  const getUserPreferences = useCallback(() => {
    if (!isReady) return persistentStorage.getUserPreferences();
    return persistentStorage.getUserPreferences();
  }, [isReady]);

  // Store dashboard state
  const storeDashboardState = useCallback((state) => {
    if (!isReady) return false;
    return persistentStorage.storeDashboardState(state);
  }, [isReady]);

  // Get dashboard state
  const getDashboardState = useCallback(() => {
    if (!isReady) return persistentStorage.getDashboardState();
    return persistentStorage.getDashboardState();
  }, [isReady]);

  // Store last visited page
  const storeLastVisitedPage = useCallback((page) => {
    if (!isReady) return false;
    return persistentStorage.storeLastVisitedPage(page);
  }, [isReady]);

  // Get last visited page
  const getLastVisitedPage = useCallback(() => {
    if (!isReady) return persistentStorage.getLastVisitedPage();
    return persistentStorage.getLastVisitedPage();
  }, [isReady]);

  // Store broker connections
  const storeBrokerConnections = useCallback((connections) => {
    if (!isReady) return false;
    return persistentStorage.storeBrokerConnections(connections);
  }, [isReady]);

  // Get broker connections
  const getBrokerConnections = useCallback(() => {
    if (!isReady) return persistentStorage.getBrokerConnections();
    return persistentStorage.getBrokerConnections();
  }, [isReady]);

  // Check if user has active broker connections
  const hasActiveBrokerConnections = useCallback(() => {
    if (!isReady) return false;
    return persistentStorage.hasActiveBrokerConnections();
  }, [isReady]);

  // Store session info
  const storeSessionInfo = useCallback((sessionInfo) => {
    if (!isReady) return false;
    return persistentStorage.storeSessionInfo(sessionInfo);
  }, [isReady]);

  // Get session info
  const getSessionInfo = useCallback(() => {
    if (!isReady) return persistentStorage.getSessionInfo();
    return persistentStorage.getSessionInfo();
  }, [isReady]);

  // Store Strategy Center data
  const storeStrategyCenterData = useCallback((stocks) => {
    if (!isReady) return false;
    return persistentStorage.storeStrategyCenterData(stocks);
  }, [isReady]);

  // Get Strategy Center data
  const getStrategyCenterData = useCallback(() => {
    if (!isReady) return persistentStorage.getStrategyCenterData();
    return persistentStorage.getStrategyCenterData();
  }, [isReady]);

  // Clear all persistent data
  const clearAllData = useCallback(() => {
    if (!isReady) return false;
    return persistentStorage.clearAll();
  }, [isReady]);

  // Auto-save form data on component unmount
  const useAutoSaveForm = useCallback((formName, formData, dependencies = []) => {
    useEffect(() => {
      return () => {
        // Save form data when component unmounts
        if (formData && Object.keys(formData).length > 0) {
          storeFormData(formName, formData);
          console.log(`💾 Auto-saved form data: ${formName}`);
        }
      };
    }, [formName, formData, ...dependencies]);
  }, [storeFormData]);

  // Auto-save dashboard state (deprecated - use direct useEffect instead)
  const useAutoSaveDashboard = useCallback((dashboardState, dependencies = []) => {
    useEffect(() => {
      return () => {
        // Save dashboard state when component unmounts
        if (dashboardState && Object.keys(dashboardState).length > 0) {
          storeDashboardState(dashboardState);
          console.log('💾 Auto-saved dashboard state');
        }
      };
    }, [dashboardState, ...dependencies]);
  }, [storeDashboardState]);

  return {
    isReady,
    // Form data management
    storeFormData,
    getFormData,
    useAutoSaveForm,
    
    // User preferences
    storeUserPreferences,
    getUserPreferences,
    
    // Dashboard state
    storeDashboardState,
    getDashboardState,
    useAutoSaveDashboard,
    
    // Navigation
    storeLastVisitedPage,
    getLastVisitedPage,
    
    // Broker connections
    storeBrokerConnections,
    getBrokerConnections,
    hasActiveBrokerConnections,
    
    // Session info
    storeSessionInfo,
    getSessionInfo,
    
    // Strategy Center data
    storeStrategyCenterData,
    getStrategyCenterData,
    
    // Utility
    clearAllData
  };
};

export default usePersistentData; 